import { z } from 'zod';
import {
  dataHoraSchema,
  dataSchema,
  paginacaoSchema,
  respostaListaSchema,
  uuidSchema,
} from './comum.ts';

/**
 * Log de alterações (`.spec/017`) — a leitura.
 *
 * A gravação não passa por aqui: a mudança física é escrita por trigger e o
 * gesto de negócio por `lib/auditoria.ts`, os dois no servidor. Este contrato
 * descreve só o que a tela consulta, e é por isso que não existe schema de
 * entrada — **o log não tem POST, PUT nem DELETE**, e essa ausência é a
 * funcionalidade.
 *
 * A tabela se chama `audit_log` porque é o nome que `.spec/001` seção 6 fixou;
 * o arquivo e a tela falam "log de alterações", que é como o usuário chama. Os
 * dois nomes convivem de propósito — renomear a tabela agora quebraria as duas
 * specs que a citam.
 */

/**
 * As ações registradas.
 *
 * **Espelha `ACOES_AUDITORIA` em `apps/api/src/db/schema/audit-log.ts`**, que
 * alimenta o `CHECK` da coluna. As duas listas têm de andar juntas; o schema do
 * banco não importa daqui porque nenhum arquivo de `db/schema` importa deste
 * pacote, e abrir essa porta por causa de oito literais custaria mais do que a
 * repetição.
 */
export const acaoDeAuditoriaSchema = z.enum([
  'INSERT',
  'UPDATE',
  'DELETE',
  'CONFIRM',
  'CANCEL',
  'LOGIN',
  'LOGIN_FAIL',
  'CONSULTA',
  'CONVITE',
  'SENHA',
  'MFA',
  'SESSAO',
]);

export type AcaoDeAuditoria = z.infer<typeof acaoDeAuditoriaSchema>;

/**
 * Como cada ação se lê na tela.
 *
 * Mora aqui, e não no componente, pela regra do projeto: dois nomes para a
 * mesma ação é o que acontece quando a API e a web escrevem cada uma o seu.
 */
export const ROTULO_ACAO: Readonly<Record<AcaoDeAuditoria, string>> = {
  INSERT: 'Inclusão',
  UPDATE: 'Alteração',
  DELETE: 'Exclusão',
  CONFIRM: 'Confirmação',
  CANCEL: 'Cancelamento',
  LOGIN: 'Entrada no sistema',
  LOGIN_FAIL: 'Entrada recusada',
  CONSULTA: 'Consulta ao log',
  CONVITE: 'Convite enviado',
  SENHA: 'Senha definida',
  MFA: 'Segundo fator',
  SESSAO: 'Sessão encerrada',
};

/**
 * O nome de tabela como o usuário o reconhece.
 *
 * Parcial de propósito: são 57 tabelas auditadas, e a maioria é filha de
 * documento — `notas_compra_itens_impostos` não tem nome de tela porque não é
 * tela. Batizar as 57 produziria vinte rótulos inventados que ninguém usa e que
 * envelhecem sem ninguém notar.
 *
 * O que não está aqui cai em `rotuloDaEntidade`, que devolve o **nome técnico**
 * da tabela. É deliberado: quem lê o log de uma tabela filha está investigando,
 * e o nome técnico é o que ele precisa para achar a coluna. Um rótulo bonito e
 * errado seria pior.
 */
export const ROTULO_ENTIDADE: Readonly<Record<string, string>> = {
  pessoas: 'Pessoa',
  usuarios: 'Usuário',
  usuarios_papeis: 'Papéis do usuário',
  usuarios_empresas: 'Empresas do usuário',
  configuracao_conta: 'Configuração da conta',
  obras: 'Obra',
  contratos: 'Contrato',
  insumos: 'Insumo',
  grupos_insumos: 'Grupo de insumos',
  depositos: 'Depósito',
  operacoes_fiscais: 'Operação fiscal',
  contas_contabeis: 'Conta contábil',
  contas_correntes: 'Conta corrente',
  historicos_bancarios: 'Histórico bancário',
  historicos_financeiros: 'Histórico financeiro',
  solicitacoes_compra: 'Solicitação de compra',
  cotacoes_preco: 'Cotação de preço',
  pedidos_compra: 'Pedido de compra',
  notas_compra: 'Nota de compra',
  contas_pagar: 'Título a pagar',
  contas_receber: 'Título a receber',
  notas_servico: 'Nota de serviço',
  empresas_fiscal: 'Dados fiscais da empresa',
  baixas_contas_pagar: 'Baixa de título a pagar',
  baixas_contas_receber: 'Baixa de título a receber',
  lancamentos_estoque: 'Lançamento de estoque',
  lancamentos_consumo: 'Consumo de obra',
  devolucoes_consumo: 'Devolução de consumo',
  lancamentos_conta_corrente: 'Lançamento em conta corrente',
  movimentacoes_estoque: 'Movimentação de estoque',
  movimentacoes_conta_corrente: 'Movimentação de conta corrente',
  transferencias_estoque: 'Transferência de estoque',
  transferencias_conta_corrente: 'Transferência entre contas',
};

export function rotuloDaEntidade(entidade: string): string {
  return ROTULO_ENTIDADE[entidade] ?? entidade;
}

/** Uma linha do log — o que o trigger ou o service gravou. */
export const registroDeAlteracaoSchema = z.object({
  id: uuidSchema,
  /** Ordem de gravação. É por ela que tudo se ordena — ver o schema da tabela. */
  seq: z.number().int(),
  ocorridoEm: dataHoraSchema,
  usuarioId: uuidSchema.nullable(),
  /** Resolvido na consulta. Nulo quando o usuário foi excluído da conta. */
  usuarioNome: z.string().nullable(),
  entidade: z.string(),
  entidadeId: uuidSchema,
  acao: acaoDeAuditoriaSchema,
  /**
   * Só as colunas que mudaram, e nunca as sensíveis (`.spec/017`, seções 7 e 8).
   *
   * `record(unknown)` porque o formato é o da tabela auditada, e são 57
   * formatos diferentes. Tipar isto exigiria uma união de 57 schemas que
   * envelheceria a cada coluna nova, para uma tela que só exibe pares
   * campo/valor.
   */
  antes: z.record(z.string(), z.unknown()).nullable(),
  depois: z.record(z.string(), z.unknown()).nullable(),
  /**
   * O nome por trás do id, para as colunas que apontam para outra tabela.
   *
   * Gravado pelo trigger no instante da alteração, não resolvido nesta consulta:
   * ver o comentário da coluna em `db/schema/audit-log.ts`. Aqui isso tem uma
   * consequência prática — **a tela pode confiar no valor**. Ele é o que valia
   * quando o registro mudou, inclusive para registro que já não existe.
   *
   * Parcial e opcional por natureza: só a coluna que tem chave estrangeira e
   * cujo alvo tem nome entra. Toda leitura tem de cair no id quando a chave não
   * está aqui.
   */
  rotulos: z
    .record(z.string(), z.object({ antes: z.string().nullable(), depois: z.string().nullable() }))
    .nullable(),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
  requestId: z.string().nullable(),
});

export type RegistroDeAlteracao = z.infer<typeof registroDeAlteracaoSchema>;

/**
 * Um **gesto** — a linha que a listagem mostra.
 *
 * A listagem não devolve linha física, e é o ponto do desenho. Salvar um pedido
 * de vinte itens escreve dezenas de registros; devolvê-los crus faria a página
 * de cinquenta caber em dois salvamentos, e a paginação passaria a contar
 * linhas de imposto em vez de coisas que alguém fez. O agrupamento é por
 * `request_id`, que é igual em todas as escritas de uma requisição.
 *
 * Escrita de manutenção — seed, importação, script — não tem `request_id`, e aí
 * cada linha é o seu próprio gesto. É a leitura certa: ali não houve requisição
 * que as reunisse.
 */
export const gestoDeAlteracaoSchema = z.object({
  /** `requestId`, ou o id da linha quando ela não tem um. Opaco para a tela. */
  chave: z.string(),
  seq: z.number().int(),
  ocorridoEm: dataHoraSchema,
  usuarioId: uuidSchema.nullable(),
  usuarioNome: z.string().nullable(),
  requestId: z.string().nullable(),
  /** Quantas linhas o gesto escreveu. */
  registros: z.number().int(),
  /** As tabelas tocadas, em ordem alfabética. */
  entidades: z.array(z.string()),
  /** As ações presentes, em ordem alfabética. */
  acoes: z.array(acaoDeAuditoriaSchema),
});

export type GestoDeAlteracao = z.infer<typeof gestoDeAlteracaoSchema>;

/**
 * O período é obrigatório, e não é burocracia.
 *
 * A tabela retém cinco anos. Uma listagem sem recorte de data varre tudo para
 * devolver cinquenta linhas, e o custo cresce todo mês sem que ninguém perceba
 * — até o dia em que a tela demora. Exigir o período põe o limite onde ele se
 * enxerga, e a tela abre com o mês corrente preenchido.
 *
 * **`ZodObject`, e a regra entre campos fica de fora.** A primeira versão
 * fechava com `.refine((q) => q.de <= q.ate)`, o que transforma o schema num
 * `ZodEffects` — e aí `query-estrita.test.ts` o pescou: a varredura que confere
 * se toda consulta recusa parâmetro desconhecido reconhece consulta pela forma,
 * e o que não é `ZodObject` sai dela. O schema **continuaria** estrito, porque o
 * `ZodEffects` delega ao objeto de dentro; o que se perderia é a **verificação**,
 * e com ela a chance de alguém afrouxar o objeto depois sem nada acusar.
 *
 * Trocar a estrutura para calar o teste seria desfazer o guarda-corpo pelo lado
 * de dentro. A regra foi para onde ela rende mais — `periodoValido`, logo
 * abaixo, aplicada no service e na tela. Ganha-se de quebra uma recusa com nome
 * (`periodo_invertido`) e mensagem escrita, em vez de um issue genérico do Zod;
 * e a documentação passa a receber este objeto direto, sem o segundo schema que
 * a versão anterior precisava inventar para o conversor de OpenAPI.
 */
export const listarLogAlteracoesQuerySchema = paginacaoSchema.omit({ q: true, sort: true }).extend({
  de: dataSchema,
  ate: dataSchema,
  usuarioId: uuidSchema.optional(),
  entidade: z.string().trim().min(1).max(63).optional(),
  entidadeId: uuidSchema.optional(),
  acao: acaoDeAuditoriaSchema.optional(),
  requestId: z.string().trim().min(1).max(200).optional(),
});

export type ListarLogAlteracoesQuery = z.infer<typeof listarLogAlteracoesQuerySchema>;

/**
 * Quantas linhas físicas a exportação aceita levar.
 *
 * **Teto, e não paginação.** Um arquivo de evidência paginado não é evidência —
 * quem confere teria de juntar as partes e o hash de cada uma provaria um
 * pedaço. Então ou o recorte cabe inteiro, ou a exportação recusa e pede um
 * período menor.
 *
 * Dez mil é o número que cabe numa planilha sem sofrimento e que o servidor
 * monta em memória sem susto — o log tem `jsonb` nas duas pontas, e uma nota de
 * 30 itens sozinha escreve ~200 linhas. Acima disso o recorte pedido quase
 * sempre é "o ano inteiro", que não é o que se leva a uma perícia.
 */
export const TETO_EXPORTACAO_LOG = 10_000;

/**
 * A exportação usa o **mesmo recorte** da listagem, sem página.
 *
 * Derivado por `omit`, e não escrito de novo: filtro novo na listagem passa a
 * valer na exportação sozinho, e o dia em que os dois divergirem é o dia em que
 * alguém exporta um recorte diferente do que viu na tela.
 */
export const exportarLogAlteracoesQuerySchema = listarLogAlteracoesQuerySchema.omit({
  page: true,
  pageSize: true,
});

export type ExportarLogAlteracoesQuery = z.infer<typeof exportarLogAlteracoesQuerySchema>;

/**
 * O período faz sentido? A tela e o servidor perguntam o mesmo.
 *
 * Função exportada, e não duas comparações iguais: a tela precisa disto para
 * travar o botão antes de chamar, e o servidor precisa porque a fronteira dele
 * vale também para quem chama a API direto. Duas cópias divergiriam na primeira
 * vez que alguém decidisse aceitar o período de um dia só.
 *
 * Datas em `AAAA-MM-DD` comparam certo como texto — é o que o formato ISO
 * garante, e é por isso que não há conversão para `Date` aqui.
 */
export function periodoValido(de: string, ate: string): boolean {
  return de <= ate;
}

/** O histórico de um registro só — a aba dentro do cadastro. Sem recorte de data. */
export const historicoDeRegistroQuerySchema = paginacaoSchema.omit({ q: true, sort: true });

export type HistoricoDeRegistroQuery = z.infer<typeof historicoDeRegistroQuerySchema>;

export const listaGestosDeAlteracaoSchema = respostaListaSchema(gestoDeAlteracaoSchema);
export const listaRegistrosDeAlteracaoSchema = respostaListaSchema(registroDeAlteracaoSchema);

export type ListaGestosDeAlteracao = z.infer<typeof listaGestosDeAlteracaoSchema>;
export type ListaRegistrosDeAlteracao = z.infer<typeof listaRegistrosDeAlteracaoSchema>;

/**
 * O detalhe de um gesto — um objeto com as linhas dentro, não o array cru.
 *
 * Um array na raiz da resposta é uma porta que não abre depois: acrescentar o
 * total, ou o resumo do gesto, obrigaria a mudar o formato e quebrar quem já
 * consome. O envelope custa uma chave e evita isso.
 */
export const gestoDetalhadoSchema = z.object({
  registros: z.array(registroDeAlteracaoSchema),
});

export type GestoDetalhado = z.infer<typeof gestoDetalhadoSchema>;

/**
 * As três ações que o trigger escreve — a **mudança física** de linha.
 *
 * O resto da lista é gesto de negócio, escrito pelo service. A diferença não é
 * classificação: nos três abaixo, `antes` e `depois` são os dois lados da mesma
 * coluna; no gesto, são o contexto do que aconteceu.
 */
const MUDANCA_FISICA = new Set<AcaoDeAuditoria>(['INSERT', 'UPDATE', 'DELETE']);

/**
 * Isto é um gesto de negócio, e não uma mudança de linha?
 *
 * **A tela precisa saber, e a razão é de leitura.** No gesto, `antes` e `depois`
 * não são os dois lados de um campo: o estorno põe em `antes` a baixa que
 * desfez e em `depois` o motivo, e mostrá-los lado a lado escreveria "Motivo
 * estorno: vazio → cheque devolvido", que é falso — o motivo não mudou de vazio
 * para nada, ele é o que o gesto registrou.
 *
 * A convenção do payload do gesto vem daí, e está escrita em `lib/auditoria.ts`:
 * **cada fato aparece de um lado só** — `antes` para o que havia, `depois` para
 * o que o gesto decidiu.
 */
export function ehGestoDeNegocio(acao: AcaoDeAuditoria): boolean {
  return !MUDANCA_FISICA.has(acao);
}

/**
 * Os fatos de um gesto, achatados numa lista de pares.
 *
 * Sem os dois lados, e em ordem alfabética como `camposAlterados` — é a mesma
 * leitura, e duas ordens diferentes na mesma tela se leem como desorganização.
 * Chave repetida nos dois lados fica com o valor de `depois`, que é o lado do
 * que o gesto decidiu.
 */
export function fatosDoGesto(registro: {
  antes: Record<string, unknown> | null;
  depois: Record<string, unknown> | null;
}): [string, unknown][] {
  const juntos = { ...(registro.antes ?? {}), ...(registro.depois ?? {}) };

  return Object.entries(juntos).sort(([a], [b]) => a.localeCompare(b));
}

/**
 * Os campos que mudaram, na ordem em que a tela os mostra.
 *
 * Função compartilhada, e não uma linha na tela: a mesma resposta alimenta a
 * lista de diferenças do detalhe e o resumo da linha, e duas implementações
 * divergiriam no caso da inclusão — onde `antes` é nulo e todo campo é uma
 * mudança.
 */
export function camposAlterados(registro: {
  antes: Record<string, unknown> | null;
  depois: Record<string, unknown> | null;
}): string[] {
  const chaves = new Set([
    ...Object.keys(registro.antes ?? {}),
    ...Object.keys(registro.depois ?? {}),
  ]);

  return [...chaves].sort((a, b) => a.localeCompare(b));
}
